import Avvvatars from "avvvatars-react";
import { Link, useParams } from "react-router-dom";
import { student_routes } from "../../../router/all_routes";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { useEffect, useMemo, useState } from "react";
import { getCourseByID } from "../../../../api/edt";
import { toast } from "sonner";
import AssignmentsManager from "./AssignmentsManager";
import { getFileAttachementByEntityId } from "../../../../api/fileAttachement";
import { CourseState, CourseStatus } from "../../../edt/scheduler";
import { openSupportInNewTab } from "../../../../helper/supportHelper";

const CourseDetails = () => {
  const routes = student_routes;

  const { id } = useParams(); // On récupère l'id de l'URL

  //Initialisation des state utilisés
  const [courseDetail, setCourseDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [refreshKey, setRefreshKey] = useState(0);

  const [filesLoading, setFilesLoading] = useState<Record<string, boolean>>({});
  const [supports, setSupports] = useState<any[]>([]);

  const [selectedMonth, setSelectedMonth] = useState<string>("Tous les mois");

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!id) return;
    const fetchCourseDetails = async () => {
      try {
        setIsLoading(true);

        const response = await getCourseByID(id);
        const courseData = response.data || [];

        setCourseDetail(courseData);

        // console.log("Course", response);
      } catch (error: any) {
        // console.log("erreur de chargement du Cours : ", error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard",
        );

        // Option: reset les salles en cas d'erreur
        setCourseDetail(null);
      } finally {
        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      }
    };

    fetchCourseDetails();
  }, [refreshKey, id]);

  const fetchSupports = async () => {
    try {
      const response = await getFileAttachementByEntityId(id, "Course");
      // console.log("Supports", response);
      setSupports(response.data || []);
    } catch (error: any) {
      // console.log("erreur de chargement des supports : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchSupports();
  }, [id]);

  const getCourseStatus = (dateCourse: any, startTime: any, endTime: any) => {
    const now = new Date();

    // On combine la date et l'heure
    const startDateTime = new Date(`${dateCourse}T${startTime}`);
    const endDateTime = new Date(`${dateCourse}T${endTime}`);

    if (now > endDateTime) {
      return "Terminé";
    } else if (now >= startDateTime && now <= endDateTime) {
      return "En cours";
    } else if (now < startDateTime) {
      return "À venir";
    }
  };

  // Voir/Télécharger des support de cours
  const handleGetSupportFile = async (fileId: string, fileName: string) => {
    try {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "open" }));

      await openSupportInNewTab({ entityType: "Course", fileName });
    } catch (error: any) {
      // console.log("Erreur lors de l'ouverture du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "" }));
    }
  };

  const getSortedAndGroupedSessions = (sessions: any[]) => {
    // 1. Tri
    const sortedSessions = [...sessions].sort((a, b) => {
      const dateA = new Date(a.date_course);
      const dateB = new Date(b.date_course);

      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }

      // Si même date, comparer start_time
      if (a.session_start.start_time !== b.session_start.start_time) {
        return a.session_start.start_time.localeCompare(
          b.session_start.start_time,
        );
      }

      // Si même start_time, comparer end_time
      return a.session_end.end_time.localeCompare(b.session_end.end_time);
    });

    // 2. Regroupement par mois
    const grouped = sortedSessions.reduce((acc: any, session) => {
      const date = new Date(session.date_course);
      const monthYear = new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
      }).format(date);
      // Capitalize first letter
      const formattedMonthYear =
        monthYear.charAt(0).toUpperCase() + monthYear.slice(1);

      if (!acc[formattedMonthYear]) {
        acc[formattedMonthYear] = [];
      }
      acc[formattedMonthYear].push(session);
      return acc;
    }, {});

    return grouped;
  };

  const availableMonths = useMemo(() => {
    if (!courseDetail?.sessions) return [];
    const months = new Set<string>();
    // On trie d'abord pour avoir les mois dans l'ordre chronologique
    const sorted = [...courseDetail.sessions].sort(
      (a: any, b: any) =>
        new Date(a.date_course).getTime() - new Date(b.date_course).getTime(),
    );

    sorted.forEach((session: any) => {
      const date = new Date(session.date_course);
      const monthYear = new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
      }).format(date);
      const formatted = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
      months.add(formatted);
    });
    return Array.from(months);
  }, [courseDetail]);

  // === Gestion du chargement ===
  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <h6 className="text-muted mb-0">Chargement du cours...</h6>
                <div className="mt-3">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => window.history.back()}
                  >
                    ← Retour aux cours
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === Gestion du cas sans données ===
  if (!isLoading && hasLoaded && !courseDetail) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="ti ti-inbox fs-2 mb-2 text-muted" />
                <h6 className="text-muted mb-0">
                  Aucun détail de cours disponible.
                </h6>
                <div className="mt-3">
                  <Link
                    to={student_routes.studentCourse}
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => window.history.back()}
                  >
                    ← Retour aux cours
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {courseDetail.course.Subject.label}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.studentCourse}>Cours</Link>
                  </li>
                  <li
                    className="breadcrumb-item active me-2"
                    aria-current="page"
                  >
                    Cours
                  </li>
                  {courseDetail.course.state === "Approuvé" ? (
                    <span className="badge badge-soft-success d-inline-flex align-items-center me-2">
                      <i className="ti ti-circle-filled fs-6 me-1" />
                      Approuvé
                    </span>
                  ) : (
                    <span className="badge badge-soft-warning d-inline-flex align-items-center me-2">
                      <i className="ti ti-circle-filled fs-6 me-1" />
                      Non Approuvé
                    </span>
                  )}
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="d-flex align-items-center justify-content-between p-3 mb-3 border br-5">
            <div className="d-flex align-items-center overflow-hidden me-2">
              <Link to="#" className="avatar avatar-lg flex-shrink-0 br-6 me-2">
                <Avvvatars
                  value={`${courseDetail.course.TeacherProfil.firstname?.charAt(0) || ""}${courseDetail.course.TeacherProfil.lastname?.charAt(0) || ""}`}
                  style="character"
                  size={40}
                />
              </Link>
              <div className="overflow-hidden">
                <h6 className="mb-1 text-truncate">
                  <Link to="#">
                    {courseDetail.course.TeacherProfil.firstname}{" "}
                    {courseDetail.course.TeacherProfil.lastname}
                  </Link>
                </h6>
                <p>
                  {courseDetail.course.Classes.length > 0
                    ? courseDetail.course.Classes.map(
                        (classe: any) =>
                          `${classe.Level.abbreviation} ${classe.abbreviation}`,
                      ).join(", ")
                    : "Aucune Classe"}
                </p>
              </div>
            </div>
          </div>

          <div className="col-xxl-12 col-xl-12">
            <div className="card">
              <div className="card-body">
                <ul
                  className="nav nav-tabs tab-style-2 nav-justified mb-3 d-flex"
                  id="myTab1"
                  role="tablist"
                >
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="seance-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#seance-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="seance-tab-pane"
                      aria-selected="true"
                    >
                      <i className="feather-clock me-1 align-middle" />
                      <span className="d-none d-md-inline">Séances</span>
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link "
                      id="support-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#support-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="support-tab-pane"
                      aria-selected="false"
                    >
                      <i className="feather-file-text me-1 align-middle" />
                      <span className="d-none d-md-inline">Support</span>
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="devoir-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#devoir-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="devoir-tab-pane"
                      aria-selected="false"
                    >
                      <i className="feather-check-square me-1 align-middle" />
                      <span className="d-none d-md-inline">Dévoirs & TD</span>
                    </button>
                  </li>
                </ul>

                <div className="tab-content" id="myTabContent">
                  {/* Liste des séances de ce cours */}
                  <div
                    className="tab-pane fade show active  text-muted"
                    id="seance-tab-pane"
                    role="tabpanel"
                    aria-labelledby="home-tab-1"
                    tabIndex={0}
                  >
                    <div className=" theiaStickySidebar">
                      <div className="stickybar">
                        {/* Filtre par mois */}
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <label className="form-label fw-semibold mb-0">
                            Filtrer par mois
                          </label>

                          <select
                            className="form-select w-auto"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                          >
                            <option value="Tous les mois">Tous les mois</option>
                            {availableMonths.map((month) => (
                              <option key={month} value={month}>
                                {month}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Event Item */}
                        {courseDetail.sessions.length > 0 ? (
                          Object.entries(
                            getSortedAndGroupedSessions(courseDetail.sessions),
                          )
                            .filter(
                              ([month]) =>
                                selectedMonth === "Tous les mois" ||
                                month === selectedMonth,
                            )
                            .map(([month, sessions]: [string, any]) => (
                              <div key={month} className="mb-4">
                                <h5 className="mb-3 text-primary ps-3 border-start border-3 border-primary">
                                  {month}
                                </h5>
                                <div className="row g-4">
                                  {sessions.map(
                                    (seance: any, index: number) => {
                                      const courseStatus = getCourseStatus(
                                        seance.date_course,
                                        seance.session_start.start_time,
                                        seance.session_end.end_time,
                                      );
                                      const courseState = seance.state_course;

                                      const courseType = seance.type_course;

                                      const badgeClass =
                                        courseStatus === "Terminé"
                                          ? "badge-soft-success"
                                          : courseStatus === "En cours"
                                            ? "badge-soft-info"
                                            : "badge-soft-warning";

                                      const typeBadgeClass =
                                        courseType === CourseState.NORMAL
                                          ? "badge-soft-primary"
                                          : "badge-soft-warning";

                                      const stateBadgeClass =
                                        courseState === CourseStatus.SCHEDULED
                                          ? "badge-soft-success"
                                          : "badge-soft-danger";

                                      const borderClass =
                                        courseStatus === "Terminé"
                                          ? "border-success"
                                          : courseStatus === "En cours"
                                            ? "border-info"
                                            : "border-warning";

                                      const avatarClass =
                                        courseStatus === "Terminé"
                                          ? "bg-success-transparent text-success"
                                          : courseStatus === "En cours"
                                            ? "bg-info-transparent text-info"
                                            : "bg-warning-transparent text-warning";
                                      return (
                                        <div
                                          key={index}
                                          className="col-12 col-md-6 col-lg-4 col-xl-4"
                                        >
                                          <div
                                            className={`border-start ${borderClass} border-3 shadow-sm p-3 bg-white h-100`}
                                          >
                                            <div className="d-flex flex-column align-items-start mb-3 pb-3 border-bottom">
                                              <div className="d-flex  justify-content-between mb-3 pb-3 w-100">
                                                <span
                                                  className={`avatar p-1 me-3 ${avatarClass} flex-shrink-0`}
                                                >
                                                  <i className="ti ti-chalkboard fs-20" />
                                                </span>
                                                <div className="flex-fill">
                                                  <div className="d-flex justify-content-between align-items-start">
                                                    <h6 className="mb-1">
                                                      Séance {index + 1}
                                                    </h6>

                                                    <span
                                                      className={`badge d-inline-flex align-items-center ms-2 ${typeBadgeClass}`}
                                                    >
                                                      <i className="ti ti-circle-filled fs-6 me-1" />
                                                      {courseType}
                                                    </span>
                                                    <span
                                                      className={`badge d-inline-flex align-items-center ms-2 ${badgeClass}`}
                                                    >
                                                      <i className="ti ti-circle-filled fs-6 me-1" />
                                                      {courseStatus}
                                                    </span>
                                                  </div>
                                                  <p className="fs-12 mb-0">
                                                    <i className="ti ti-calendar me-1" />
                                                    {seance.classroom.label}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="d-flex align-items-center justify-content-between">
                                              <p className="mb-0 fs-12">
                                                <i className="ti ti-clock me-1" />
                                                {new Date(seance.date_course)
                                                  .toLocaleDateString("fr-FR", {
                                                    day: "numeric",
                                                    month: "long",
                                                    year: "numeric",
                                                  })
                                                  .replace(/^./, (str) =>
                                                    str.toUpperCase(),
                                                  )}
                                                {" [ " +
                                                  seance.session_start.start_time?.slice(
                                                    0,
                                                    5,
                                                  ) +
                                                  " - " +
                                                  seance.session_end.end_time?.slice(
                                                    0,
                                                    5,
                                                  ) +
                                                  " ]"}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    },
                                  )}
                                </div>
                              </div>
                            ))
                        ) : (
                          <div className="border-start border-secondary border-3 shadow-sm p-3 mb-3 bg-white">
                            <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                              <span className="avatar p-1 me-3 bg-secondary-transparent flex-shrink-0">
                                <i className="ti ti-inbox text-secondary fs-20" />
                              </span>
                              <div className="flex-fill text-center">
                                <h6 className="mb-1">Aucune Séance</h6>
                                <p className="fs-12 mb-0 text-muted">
                                  Il n’y a pas encore de séance programmée pour
                                  ce cours.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* /Event List */}
                    </div>
                  </div>

                  {/* Supports de ce cours */}
                  <div
                    className="tab-pane fade text-muted"
                    id="support-tab-pane"
                    role="tabpanel"
                    aria-labelledby="profile-tab-2"
                    tabIndex={0}
                  >
                    <div className="mb-4 pb-4 border-bottom">
                      <div className="d-flex align-items-center mb-3">
                        <h4>Supports de cours</h4>
                        <div className="owl-nav slide-nav7 text-end nav-control ms-3" />
                      </div>
                      {supports && supports.length > 0 ? (
                        supports.map((file: any) => {
                          const fileExt = file?.filename
                            ?.split(".")
                            .pop()
                            ?.toLowerCase();
                          const iconMap: Record<string, string> = {
                            pdf: "assets/img/icons/pdf-02.svg",
                            xls: "assets/img/icons/xls.svg",
                            xlsx: "assets/img/icons/xls.svg",
                            doc: "assets/img/icons/word.svg",
                            docx: "assets/img/icons/word.svg",
                            ppt: "assets/img/icons/ppt.svg",
                            pptx: "assets/img/icons/ppt.svg",
                          };
                          const iconSrc =
                            iconMap[fileExt] || "assets/img/icons/folder.svg";

                          const formatBytes = (bytes?: number) => {
                            if (!bytes) return " ";
                            if (bytes < 1024) return bytes + " B";
                            const kb = bytes / 1024;
                            if (kb < 1024) return kb.toFixed(1) + " KB";
                            const mb = kb / 1024;
                            return mb.toFixed(1) + " MB";
                          };

                          return (
                            <div
                              key={file.id}
                              className="border rounded-3 bg-white p-3 mb-2"
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                  <ImageWithBasePath
                                    src={iconSrc}
                                    alt="File"
                                    className="me-2 flex-shrink-0"
                                  />
                                  <h5 className="mb-0 text-truncate flex-grow-1">
                                    <Link
                                      to="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleGetSupportFile(
                                          file?.id,
                                          file?.filename,
                                        );
                                      }}
                                    >
                                      {file.filename}
                                    </Link>
                                  </h5>
                                </div>

                                <div className="d-flex align-items-center ms-2 flex-shrink-0">
                                  <div className="dropdown">
                                    <Link
                                      to="#"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      className="dropset"
                                    >
                                      <i className="fa fa-ellipsis-v" />
                                    </Link>
                                    <ul className="dropdown-menu">
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleGetSupportFile(
                                              file?.id,
                                              file?.filename,
                                            )
                                          }
                                        >
                                          Télécharger
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex align-items-center justify-content-start mt-3">
                                <p className="text-primary mb-0 me-2">
                                  {new Date(file.createdAt).toLocaleDateString(
                                    "fr-FR",
                                  )}
                                </p>
                                <span className="d-flex align-items-center fw-semibold me-2">
                                  <i className="ti ti-circle-filled fs-5 me-2" />
                                  {formatBytes(file.size)}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="border rounded-3 bg-white p-3 mb-2 text-center text-muted">
                          Aucun support de cours disponible.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dévoirs & TD de ce cours */}
                  <div
                    className="tab-pane fade text-muted"
                    id="devoir-tab-pane"
                    role="tabpanel"
                    aria-labelledby="contact-tab"
                    tabIndex={0}
                  >
                    {courseDetail && (
                      <AssignmentsManager
                        courseId={id || ""}
                        classes={courseDetail.course.Classes}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseDetails;
